import { canWriteProperties } from '@/api/core/authorization';
import {
  readEzremsBindingContext,
  type EzremsBindingCandidate,
  type EzremsBindingContext,
  type EzremsBindingTarget,
} from '@/api/core/ezrems-binding-context';
import {
  bindEzremsRoom,
  parseEzremsBindingReceipt,
  validateEzremsBindingCommand,
  type EzremsRoomBindingCommand,
} from '@/api/core/ezrems-room-binding';
import { isApiError } from '@/api/http/errors';
import { isUuid } from '@/lib/validation';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mgrvtechgroup/admin-ds';
import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { freezeRequest } from './presentation/command-owner';
import type { PropertyRuntime } from './property-queries';

interface Props {
  readEnabled?: boolean;
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  typeId: string;
  typeName: string;
  room?: { id: string; name: string; units: { name: string }[] };
  onBindingChanged?: () => void;
  onGoSales?: () => void;
}
interface Intent {
  roomId: string;
  actorId: string;
  generation: number;
  namespace?: string;
  command: EzremsRoomBindingCommand;
}
const originalBindings = new WeakMap<
  Intent,
  {
    command: EzremsRoomBindingCommand;
    json: string;
    actor: string;
    namespace: string;
    generation: number;
    roomId: string;
    client: PropertyRuntime['client'];
  }
>();
export const currentAccess = (runtime: PropertyRuntime, generation: number) => {
  const auth = runtime.session.getSnapshot();
  return (
    auth.generation === generation &&
    auth.status === 'authorized' &&
    auth.authorization !== null &&
    !auth.deniedDomains.includes('property') &&
    canWriteProperties(auth.authorization)
  );
};
const snapshot = (context: EzremsBindingContext) =>
  JSON.stringify({
    room_id: context.room_id,
    room_version: context.room_version,
    binding_revision: context.binding_revision,
    binding: context.binding,
    scope: context.scope,
    unit_ids: context.unit_ids,
    targets: context.targets,
  });

export function EzremsRoomBindingSection(props: Props) {
  const auth = useSyncExternalStore(
    props.runtime.session.subscribe,
    props.runtime.session.getSnapshot,
    props.runtime.session.getSnapshot,
  );
  const [held, setHeld] = useState<Intent | null>(null);
  // 같은 원 요청의 재시도를 서버가 확정 거절(409)했다 — 반영 여부는 이 화면이 단정하지 못한다.
  const [heldRefused, setHeldRefused] = useState(false);
  const [sending, setSending] = useState(false);
  const lock = useRef(false);
  const [feedbackState, setFeedbackState] = useState<{ key: string; text: string } | null>(null);
  const [receiptRoom, setReceiptRoom] = useState<string | null>(null);
  const allowed = currentAccess(props.runtime, props.generation);
  const ownerKey = JSON.stringify([
    props.runtime.apiNamespace,
    props.generation,
    props.propertyId,
    props.typeId,
    props.room?.id,
    auth.generation,
    auth.status,
    auth.authorization,
    auth.deniedDomains,
  ]);
  const owner = useRef(ownerKey);
  owner.current = ownerKey;
  const feedback = feedbackState?.key === ownerKey ? feedbackState.text : '';
  const setFeedback = (text: string) => {
    setFeedbackState({ key: ownerKey, text });
  };
  const send = async (intent: Intent, refresh: () => Promise<void>) => {
    if (
      lock.current ||
      !currentAccess(props.runtime, intent.generation) ||
      intent.roomId !== props.room?.id
    )
      return;
    lock.current = true;
    setSending(true);
    const startedOwner = ownerKey;
    const replayingHeldCommand = held === intent;
    let attempted = false;
    try {
      const actor = await props.runtime.client.get<{ user_id?: unknown }>('users/me');
      if (
        owner.current !== startedOwner ||
        actor.user_id !== intent.actorId ||
        !currentAccess(props.runtime, intent.generation)
      ) {
        setFeedback('원래 수행자와 권한을 확인할 수 없습니다. 원래 요청을 보존했습니다.');
        return;
      }
      if (replayingHeldCommand) {
        let original = originalBindings.get(intent);
        if (
          !original &&
          intent.namespace === props.runtime.apiNamespace &&
          Object.isFrozen(intent.command)
        ) {
          original = {
            command: intent.command,
            json: JSON.stringify(intent.command),
            actor: intent.actorId,
            namespace: intent.namespace,
            generation: intent.generation,
            roomId: intent.roomId,
            client: props.runtime.client,
          };
          originalBindings.set(intent, original);
        }
        if (
          original?.actor !== actor.user_id ||
          original.namespace !== props.runtime.apiNamespace ||
          original.generation !== props.generation ||
          original.roomId !== intent.roomId ||
          original.client !== props.runtime.client ||
          original.command !== intent.command ||
          original.json !== JSON.stringify(intent.command)
        )
          throw new Error('원래 연결 요청이 변경되었습니다.');
        attempted = true;
        parseEzremsBindingReceipt(
          await props.runtime.client.post<unknown>(
            `admin/rooms/${original.roomId}/ezrems-binding`,
            { json: original.command, expectedStatus: 200 },
          ),
          original.roomId,
          original.command,
        );
      } else {
        validateEzremsBindingCommand(intent.roomId, intent.command);
        freezeRequest(intent.command);
        intent.namespace = props.runtime.apiNamespace;
        originalBindings.set(intent, {
          command: intent.command,
          json: JSON.stringify(intent.command),
          actor: intent.actorId,
          namespace: props.runtime.apiNamespace,
          generation: intent.generation,
          roomId: intent.roomId,
          client: props.runtime.client,
        });
        setHeld(intent);
        attempted = true;
        await bindEzremsRoom(props.runtime.client, intent.roomId, intent.command);
      }
      setHeld(null);
      setHeldRefused(false);
      if (owner.current === startedOwner) {
        props.onBindingChanged?.();
        setReceiptRoom(intent.roomId);
        setFeedback(
          intent.command.expected_binding_revision === 0
            ? '최초 연결 응답을 확인했습니다. 현재 연결 정보를 다시 조회합니다.'
            : '재연결 응답을 확인했습니다. 현재 연결 정보를 다시 조회합니다.',
        );
        await refresh();
      }
    } catch (error) {
      const unknown = !isApiError(error) || error.kind !== 'http' || (error.status ?? 500) >= 500;
      if (attempted && !unknown && !replayingHeldCommand) setHeld(null);
      const refusedReplay =
        attempted && replayingHeldCommand && isApiError(error) && error.status === 409;
      if (refusedReplay) setHeldRefused(true);
      if (owner.current === startedOwner) {
        if (isApiError(error) && error.status === 403)
          props.runtime.session.reportDomainAccessDenied('property', props.generation);
        setFeedback(
          refusedReplay
            ? '서버가 같은 요청을 현재 연결 상태와 맞지 않는다며 거절했습니다. 다른 변경이 먼저 반영됐거나 원래 요청이 이미 반영됐을 수 있어, 새 요청은 보내지 않았습니다. 요청을 폐기하고 현재 연결을 다시 조회해 확인하세요.'
            : (attempted && unknown) || (attempted && replayingHeldCommand)
              ? '연결 결과가 불명확합니다. 처음 요청과 입력을 보존했으며 자동 재전송하지 않습니다.'
              : '연결 정보를 다시 조회하고 후보를 새로 선택하세요.',
        );
      }
    } finally {
      lock.current = false;
      setSending(false);
    }
  };
  const discardHeld = (refresh: () => Promise<void>) => {
    if (lock.current) return;
    setHeld(null);
    setHeldRefused(false);
    setFeedback('원래 요청을 폐기했습니다. 반영 여부는 다시 조회한 현재 연결로 확인하세요.');
    void refresh();
  };
  return (
    <BindingDraft
      key={ownerKey}
      {...props}
      heldRefused={heldRefused}
      discardHeld={discardHeld}
      allowed={allowed}
      blocked={held !== null || sending || receiptRoom === props.room?.id}
      sending={sending}
      feedback={feedback}
      held={held}
      send={send}
    />
  );
}

function BindingDraft(
  props: Props & {
    allowed: boolean;
    blocked: boolean;
    sending: boolean;
    feedback: string;
    held: Intent | null;
    heldRefused: boolean;
    discardHeld: (refresh: () => Promise<void>) => void;
    send: (intent: Intent, refresh: () => Promise<void>) => Promise<void>;
  },
) {
  const [context, setContext] = useState<EzremsBindingContext | null>(null);
  const [target, setTarget] = useState<EzremsBindingTarget | null>(null);
  const [candidates, setCandidates] = useState<EzremsBindingCandidate[]>([]);
  const [candidateId, setCandidateId] = useState('');
  const [reason, setReason] = useState('');
  const reasonTooLong = Array.from(reason).length > 500;
  const reasonErrorId = useId();
  const [intent, setIntent] = useState<Intent | null>(null);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState('');
  const alive = useRef(true);
  const readActive = useRef(props.readEnabled !== false);
  readActive.current = props.readEnabled !== false;
  const isReadActive = () => readActive.current;
  const readController = useRef<AbortController | null>(null);
  const epoch = useRef(0);
  const executing = useRef(false);
  const resumeReadPending = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      readController.current?.abort();
      epoch.current += 1;
    };
  }, []);
  const valid = () => alive.current && currentAccess(props.runtime, props.generation);
  const clear = () => {
    setIntent(null);
    setCandidates([]);
    setCandidateId('');
    setTarget(null);
  };
  const check = (data: EzremsBindingContext) => {
    if (data.scope.property_id !== props.propertyId || data.scope.room_type_id !== props.typeId)
      throw new Error('Changed parent');
  };
  const failure = (error: unknown) => {
    if (!valid()) return;
    clear();
    setContext(null);
    setFeedback(
      isApiError(error) && error.status === 503
        ? 'EZREMS 연결 정보와 후보를 현재 조회할 수 없습니다. 연결 정보를 다시 조회하고 후보를 새로 선택하세요.'
        : '연결 정보를 다시 조회하고 후보를 새로 선택하세요.',
    );
    if (isApiError(error) && error.status === 403)
      props.runtime.session.reportDomainAccessDenied('property', props.generation);
  };
  const reload = async () => {
    if (!isReadActive() || !valid() || !props.room) return;
    if (executing.current) {
      resumeReadPending.current = true;
      return;
    }
    resumeReadPending.current = false;
    const request = ++epoch.current;
    readController.current?.abort();
    const controller = new AbortController();
    readController.current = controller;
    clear();
    setContext(null);
    setBusy(true);
    setFeedback('');
    try {
      const data = await readEzremsBindingContext(
        props.runtime.client,
        props.room.id,
        undefined,
        controller.signal,
      );
      check(data);
      if (isReadActive() && valid() && request === epoch.current) {
        setContext(data);
        setFeedback('');
      }
    } catch (error) {
      if (request === epoch.current) failure(error);
    } finally {
      if (alive.current && request === epoch.current) setBusy(false);
    }
  };
  const allCandidates = async (selected: EzremsBindingTarget, baseline: EzremsBindingContext) => {
    if (!props.room) throw new Error('No room');
    const requestEpoch = epoch.current;
    let cursor: number | undefined;
    const found: EzremsBindingCandidate[] = [];
    for (let pageNumber = 0; pageNumber < 1000; pageNumber += 1) {
      const page = await readEzremsBindingContext(props.runtime.client, props.room.id, {
        ...selected,
        ...(cursor === undefined ? {} : { after_resource_id: cursor }),
      });
      if (!valid() || requestEpoch !== epoch.current) throw new Error('Scope left');
      check(page);
      if (snapshot(page) !== snapshot(baseline)) throw new Error('Scope changed');
      found.push(...page.candidates);
      if (page.next_resource_id === null) return { page, found };
      cursor = page.next_resource_id;
    }
    throw new Error('Incomplete candidates');
  };
  useEffect(() => {
    if (isReadActive()) void reload();
    else {
      readController.current?.abort();
      epoch.current++;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Read suspension does not change command ownership.
  }, [props.readEnabled]);
  const selectTarget = async (value: string) => {
    const selected = context?.targets[Number(value)];
    clear();
    setFeedback('');
    const request = ++epoch.current;
    if (value === '' || !selected || !valid()) return;
    setTarget(selected);
    setBusy(true);
    try {
      const result = await allCandidates(selected, context);
      if (isReadActive() && valid() && request === epoch.current) {
        setContext(result.page);
        setCandidates(result.found);
      }
    } catch (error) {
      if (request === epoch.current) failure(error);
    } finally {
      if (alive.current && request === epoch.current) setBusy(false);
    }
  };
  const review = async () => {
    if (
      !props.room ||
      !target ||
      !context ||
      props.blocked ||
      busy ||
      executing.current ||
      !valid()
    )
      return;
    const candidate = selectable.find((c) => String(c.resource_id) === candidateId);
    if (!candidate || !reason.trim() || reasonTooLong) return;
    const requestEpoch = ++epoch.current;
    executing.current = true;
    setBusy(true);
    setIntent(null);
    setFeedback('');
    try {
      const result = await allCandidates(target, context);
      if (!result.found.some((c) => JSON.stringify(c) === JSON.stringify(candidate)))
        throw new Error('Candidate changed');
      const actor = await props.runtime.client.get<{ user_id?: unknown }>('users/me');
      if (!valid() || requestEpoch !== epoch.current || !isUuid(actor.user_id))
        throw new Error('Actor changed');
      setIntent({
        roomId: props.room.id,
        actorId: actor.user_id,
        generation: props.generation,
        command: {
          command_id: crypto.randomUUID(),
          expected_room_version: context.room_version,
          expected_binding_revision: context.binding_revision,
          scope: { ...context.scope },
          ...target,
          resource_id: candidate.resource_id,
          dong: candidate.dong,
          floor: candidate.floor,
          ho2: candidate.ho2,
          reason,
        },
      });
    } catch (error) {
      if (requestEpoch === epoch.current) failure(error);
    } finally {
      executing.current = false;
      if (resumeReadPending.current && isReadActive() && valid()) void reload();
      else {
        resumeReadPending.current = false;
        if (alive.current && requestEpoch === epoch.current) setBusy(false);
      }
    }
  };
  const rebinding = (context?.binding_revision ?? 0) > 0;
  // 현재 연결과 같은 호실로의 재설정은 서버가 409로 거절하므로 선택지에서 뺀다.
  const selectable = candidates.filter((c) => c.resource_id !== context?.binding?.resource_id);
  return (
    <section
      aria-label={rebinding ? '재연결 확인 패널' : '최초 연결 확인 패널'}
      className="body-sm space-y-4 rounded-md border p-4 leading-relaxed"
    >
      {!props.room && <p className="body-sm">{props.typeName} · 객실을 선택하세요.</p>}
      <p className="body-sm">
        {rebinding
          ? '연결된 EZREMS 호실이 다르면 새 호실로 다시 연결할 수 있습니다. 진행 중인 판매 전환이나 다른 변경이 있으면 서버가 거절할 수 있습니다.'
          : 'EZREMS의 대응 호실을 연결한 뒤 가격·판매를 설정하세요.'}
      </p>
      {!props.allowed && <p role="status">현재 지점 연결 권한이 없습니다.</p>}
      {context?.binding && (
        <>
          <p>
            현재 연결: {context.binding.branch_code} · {context.binding.project_code} ·{' '}
            {context.binding.dong} · {context.binding.floor} · {context.binding.ho2}
          </p>
          {props.onGoSales && (
            <Button className="h-auto min-h-8 whitespace-normal" onClick={props.onGoSales}>
              {props.room?.name} 가격·판매 설정
            </Button>
          )}
        </>
      )}
      {busy && <p role="status">연결 정보를 불러오고 있습니다.</p>}
      {context?.binding_revision === 0 && <p>아직 연결하지 않은 객실입니다.</p>}
      {context && !context.binding && context.targets.length === 0 && (
        <p role="status">연결할 EZREMS 지점이 설정되지 않았습니다. 지점 설정을 확인해 주세요.</p>
      )}
      {context && props.allowed && !props.blocked && (
        <>
          <div className="grid gap-1 sm:max-w-sm">
            EZREMS 프로젝트
            <Select
              disabled={busy || intent !== null || context.targets.length === 0}
              value={
                target
                  ? String(
                      context.targets.findIndex(
                        (t) => JSON.stringify(t) === JSON.stringify(target),
                      ),
                    )
                  : null
              }
              onValueChange={(value) => {
                void selectTarget(value ?? '');
              }}
              items={context.targets.map((t, i) => ({
                value: String(i),
                label: `${t.branch_code} · ${t.project_code}`,
              }))}
            >
              <SelectTrigger aria-label="EZREMS 프로젝트">
                <SelectValue placeholder="프로젝트 선택" />
              </SelectTrigger>
              <SelectContent>
                {context.targets.map((t, i) => (
                  <SelectItem key={`${String(t.branch_id)}:${t.project_code}`} value={String(i)}>
                    {t.branch_code} · {t.project_code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1 sm:max-w-sm">
            EZREMS 연결 후보
            <Select
              disabled={busy || intent !== null || !target || selectable.length === 0}
              value={candidateId || null}
              onValueChange={(value) => {
                setCandidateId(value ?? '');
                setIntent(null);
              }}
              items={selectable.map((c) => ({
                value: String(c.resource_id),
                label: `${c.dong} · ${c.floor} · ${c.ho2} · ${c.sales_kind}`,
              }))}
            >
              <SelectTrigger aria-label="EZREMS 연결 후보">
                <SelectValue placeholder="검증된 후보 선택" />
              </SelectTrigger>
              <SelectContent>
                {selectable.map((c) => (
                  <SelectItem key={c.resource_id} value={String(c.resource_id)}>
                    {c.dong} · {c.floor} · {c.ho2} · {c.sales_kind}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {target ? (
            !busy && selectable.length === 0 ? (
              <p role="status">
                선택한 프로젝트에 연결할 수 있는 호실이 없습니다. 프로젝트를 확인해 주세요.
              </p>
            ) : (
              <p className="body-sm">
                {rebinding
                  ? '새로 연결할 호실을 선택하고 재연결 사유를 입력하세요.'
                  : '연결할 호실을 선택하고 연결 사유를 입력하세요.'}
              </p>
            )
          ) : (
            <p className="body-sm">EZREMS 프로젝트를 선택하면 연결할 호실을 불러옵니다.</p>
          )}
          <label className="grid gap-1">
            EZREMS 연결 사유
            <Input
              aria-label="EZREMS 연결 사유"
              disabled={busy || intent !== null}
              value={reason}
              aria-invalid={reasonTooLong}
              aria-describedby={reasonTooLong ? reasonErrorId : undefined}
              onChange={(e) => {
                setReason(e.target.value);
                setIntent(null);
              }}
            />
          </label>
          {reasonTooLong && (
            <p id={reasonErrorId} role="alert">
              연결 사유는 500자 이내로 입력하세요.
            </p>
          )}
          <Button
            className="h-auto min-h-8 whitespace-normal"
            disabled={busy || !candidateId || !reason.trim() || reasonTooLong || intent !== null}
            onClick={() => {
              void review();
            }}
          >
            {rebinding ? '재연결 검토' : '연결 검토'}
          </Button>
        </>
      )}
      {intent && !props.blocked && (
        <fieldset
          aria-label={rebinding ? 'EZREMS 재연결 최종 확인' : 'EZREMS 최초 연결 최종 확인'}
          className="space-y-3 rounded-md border p-4"
        >
          <legend>
            {props.room?.name} 객실에 {rebinding ? '다시 연결할' : '연결할'} 호실을 확인하세요.
          </legend>
          {rebinding && context?.binding && (
            <p>
              현재 연결: {context.binding.dong} · {context.binding.floor} · {context.binding.ho2}
            </p>
          )}
          <p>
            {intent.command.branch_code} · {intent.command.project_code} · {intent.command.dong} ·{' '}
            {intent.command.floor} · {intent.command.ho2} · {intent.command.reason}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              className="h-auto min-h-8 whitespace-normal"
              disabled={!props.allowed || busy || props.sending}
              onClick={() => {
                const confirmed = intent;
                clear();
                setContext(null);
                void props.send(confirmed, reload);
              }}
            >
              {rebinding ? '재연결 저장' : '연결 저장'}
            </Button>
            <Button
              className="h-auto min-h-8 whitespace-normal"
              variant="outline"
              onClick={() => {
                setIntent(null);
              }}
            >
              검토 취소
            </Button>
          </div>
        </fieldset>
      )}
      {feedback && !busy && (
        <Button
          className="h-auto min-h-8 whitespace-normal"
          variant="outline"
          disabled={props.sending || !props.allowed}
          onClick={() => {
            void reload();
          }}
        >
          연결 정보 다시 조회
        </Button>
      )}
      {props.held && !props.heldRefused && (
        <p role="alert">연결 결과를 확인하지 못했습니다. 같은 요청으로 다시 시도할 수 있습니다.</p>
      )}
      {props.held?.roomId === props.room?.id && props.held && props.heldRefused && (
        <Button
          className="h-auto min-h-8 whitespace-normal"
          variant="outline"
          disabled={!props.allowed || props.sending}
          onClick={() => {
            props.discardHeld(reload);
          }}
        >
          요청 폐기하고 현재 연결 다시 조회
        </Button>
      )}
      {props.held?.roomId === props.room?.id && props.held && (
        <Button
          className="h-auto min-h-8 whitespace-normal"
          variant="outline"
          disabled={!props.allowed || props.sending}
          onClick={() => {
            if (props.held) void props.send(props.held, reload);
          }}
        >
          연결 요청 다시 시도
        </Button>
      )}
      {props.feedback && <p role="status">{props.feedback}</p>}
      {feedback && <p role="status">{feedback}</p>}
    </section>
  );
}
